import { useContext } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';

import { AlertContext } from './utils/AlertContextProvider';

type AlertType = 'success' | 'info' | 'warning' | 'error';
type AlertVariant = 'standard' | 'filled' | 'outlined';

interface AlertItem {
  id: number;
  text: string;
  type: AlertType;
  variant: AlertVariant;
}

interface NotificationsProps {
  customButton?: React.ReactNode;
  isCloseIcon?: boolean;
  customColor?: string;
}

interface SnackbarProviderProps extends NotificationsProps {
  alert: AlertItem;
  handleClose: (alert: AlertItem) => void;
}

export default function Notification(props: NotificationsProps) {
  const { state, actions } = useContext(AlertContext);

  const handleClose = (alert: AlertItem) => {
    actions.removeAlert(alert);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {state.alerts.map((alert, index) => (
        <SnackbarProvider
          key={`${alert.id}-${index}`}
          alert={alert}
          actions={actions}
          handleClose={handleClose}
          {...props}
        />
      ))}
    </Box>
  );
}

function SnackbarProvider({
  alert,
  handleClose,
  customButton,
  isCloseIcon = false,
  customColor,
}: SnackbarProviderProps) {
  return (
    <Alert
      elevation={6}
      variant={alert.variant}
      severity={alert.type}
      sx={customColor ? { background: customColor } : undefined}
      action={
        <>
          {customButton}
          {isCloseIcon && (
            <IconButton aria-label="close" color="inherit" size="small" onClick={() => handleClose(alert)}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          )}
        </>
      }
    >
      {alert.text}
    </Alert>
  );
}
